import React from 'react';
import styled from 'styled-components';

const PageHeaderRoot = styled.header`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-bottom: var(--space-6);
  border-bottom: 1px solid var(--color-border-subtle);
  margin-bottom: var(--space-6);
`;

const Breadcrumb = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
`;

export type PageHeaderProps = {
  title: string;
  subtitle?: string;
  breadcrumb?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumb,
  actions,
  className,
}) => {
  return (
    <PageHeaderRoot className={className}>
      {breadcrumb && <Breadcrumb>{breadcrumb}</Breadcrumb>}
      <TitleRow>
        <TitleBlock>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TitleBlock>
        {actions && <Actions>{actions}</Actions>}
      </TitleRow>
    </PageHeaderRoot>
  );
};

const SectionHeaderRoot = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
`;

const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-sans);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-snug);
  color: var(--color-text);
  margin: 0;
`;

const SectionDescription = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
`;

export type SectionHeaderProps = {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
};

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  actions,
  className,
}) => {
  return (
    <SectionHeaderRoot className={className}>
      <SectionTitleBlock>
        <SectionTitle>{title}</SectionTitle>
        {description && <SectionDescription>{description}</SectionDescription>}
      </SectionTitleBlock>
      {actions && <Actions>{actions}</Actions>}
    </SectionHeaderRoot>
  );
};
